import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useMemo } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import { CompanySearchCondition } from 'src/entities/CompanySearchCondition.entity';
import { LicenseEntity } from 'src/entities/licenseEntity';
import { useNotAllocatedOrderSearchCondition } from 'src/hooks/NotAllocatedOrderSearchCondition.mutation';

import { FilterPreset } from './presets';
import { decodePresetRows, encodePresetRows, isUnassignedFilterCondition } from './serverPresets';
import { removePresetFromSession, upsertPresetInSession } from './sessionPresetCache';
import { FilterRow } from './types';

/** 保存済みプリセット。削除にサーバの id が要るので、画面表示用の名前と一緒に持つ */
export type ServerFilterPreset = FilterPreset & { id: number };

/**
 * 一覧の出どころは license の config。プリセットを作成・削除したらこのキーを
 * invalidate してセッションを取り直す（/plans の保存共有と同じ手順）。
 */
const SESSION_QUERY_KEY = 'useSessionRequest';

export type UseServerFilterPresetsResult = {
  presets: ServerFilterPreset[];
  /**
   * 同名は上書き。`onSuccess` は CREATE が成功したときだけ呼ぶ（同名の上書きは
   * DELETE → CREATE の順で走るので、DELETE の成功では呼ばない）。旧画面の条件の
   * 取り込みが「保存できてから適用して閉じる」ために要る。
   */
  savePreset: (
    name: string,
    rows: FilterRow[],
    callbacks?: { onSuccess?: () => void; onError?: () => void },
  ) => void;
  deletePreset: (preset: ServerFilterPreset, callbacks?: { onError?: () => void }) => void;
};

/**
 * フィルタープリセットのサーバ保存。localStorage は使わない。
 *
 * 保存先は会社単位（`not_allocated_order_search_conditions`）なので、保存した条件は
 * 同じ会社の他の利用者にも見える。これはこのテーブルの元々の意味で、
 * 「常にサーバー保存」の指示はこの共有を意図している。
 */
export function useServerFilterPresets(companyId: string): UseServerFilterPresetsResult {
  const licenseContext = useContext(LicenseContext);
  const queryClient = useQueryClient();
  const { bulkCreation, destroy } = useNotAllocatedOrderSearchCondition();

  const numericCompanyId = Number(companyId);

  const presets = useMemo<ServerFilterPreset[]>(() => {
    const all: CompanySearchCondition[] = licenseContext?.config?.company_search_conditions ?? [];
    return all
      // 会社を跨いだ条件を混ぜない。旧画面が保存した条件も、列が解決できず
      // 適用すると全件消えるので出さない
      .filter((it) => it.companyId === numericCompanyId && isUnassignedFilterCondition(it))
      .map((it) => ({ id: it.id, name: it.name, rows: decodePresetRows(it.searchConditions) }));
  }, [licenseContext?.config?.company_search_conditions, numericCompanyId]);

  const refreshSession = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY_KEY] }),
    [queryClient],
  );

  /**
   * セッションのキャッシュを直接書き換える。LicenseContext は sessionData を見て
   * config を更新するので、これだけで一覧に反映される。
   * 戻り値は元に戻すための巻き戻し関数。
   */
  const patchSession = useCallback((update: (s: LicenseEntity | undefined) => LicenseEntity | undefined) => {
    const before = queryClient.getQueryData<LicenseEntity>([SESSION_QUERY_KEY]);
    queryClient.setQueryData<LicenseEntity>([SESSION_QUERY_KEY], (s) => update(s));
    return () => { queryClient.setQueryData<LicenseEntity>([SESSION_QUERY_KEY], before); };
  }, [queryClient]);

  const savePreset = useCallback<UseServerFilterPresetsResult['savePreset']>((name, rows, callbacks) => {
    const searchConditions = encodePresetRows(rows);
    // 先に画面へ出す。API の成否を待って取り直すと、保存しても数秒 画面が変わらず
    // 「効いていない」ように見える
    const rollback = patchSession((s) => upsertPresetInSession(s, { companyId: numericCompanyId, name, searchConditions }));
    const fail = () => { rollback(); callbacks?.onError?.(); };

    // API は作成しかできない（更新エンドポイントが無い）。同名を上書きするには
    // 先に消す。消してから作るので、失敗すると同名が消えたままになりうる
    const existing = presets.find((p) => p.name === name);
    // onSuccess はここ（CREATE の成功）だけで呼ぶ。同名の上書きは
    // destroy.mutate(..., { onSuccess: create }) の順で走るので、DELETE の成功で
    // 通知すると保存が終わる前に呼び出し側が適用してしまう
    const create = () => bulkCreation.mutate(
      { companyId: numericCompanyId, conditions: [{ name, searchConditions }] },
      { onSuccess: () => { void refreshSession(); callbacks?.onSuccess?.(); }, onError: fail },
    );

    if (!existing) { create(); return; }
    destroy.mutate(existing.id, { onSuccess: create, onError: fail });
  }, [bulkCreation, destroy, numericCompanyId, patchSession, presets, refreshSession]);

  const deletePreset = useCallback<UseServerFilterPresetsResult['deletePreset']>((preset, callbacks) => {
    const rollback = patchSession((s) => removePresetFromSession(s, preset.id));

    destroy.mutate(preset.id, {
      onSuccess: () => { void refreshSession(); },
      onError: () => { rollback(); callbacks?.onError?.(); },
    });
  }, [destroy, patchSession, refreshSession]);

  return { presets, savePreset, deletePreset };
}
